import { Component, OnInit, ViewChild, ElementRef } from "@angular/core";
import {
  MatDialog,
  MatDialogConfig,
  MatDialogRef,
} from "@angular/material/dialog";
import { YonyxGuide } from "../../models/yonyx-guide";
import { SafeResourceUrl, DomSanitizer } from "@angular/platform-browser";
import { Guid } from "guid-typescript";
import { WorkOrderService } from "../../../services/work-order.service";
import { ErrorService } from "services/error.service";
import { YonyxGuideInterface } from "app/interface/yonyxGuige";
import { Router } from "@angular/router";
import { WorkOrderCreate } from "app/models/work-order-create";
import { WorkOrderConfirmRequestDialogComponent } from "../work-order-confirm-request-dialog/work-order-confirm-request-dialog.component";
import { WorkOrderGrantPteDialogComponent } from "../work-order-grant-pte-dialog/work-order-grant-pte-dialog.component";
import { runInThisContext } from "vm";
import { WorkOrderSaveRetryResponse } from "../../models/work-order-save-retry-response";

@Component({
  selector: "app-work-order-create",
  templateUrl: "./work-order-create.component.html",
  styleUrls: ["./work-order-create.component.css"],
})
export class WorkOrderCreateComponent implements OnInit {
  selectedGuide: YonyxGuide = null;
  yonyxGuides: YonyxGuide[] = null;
  yonyxAppFrameSrc: SafeResourceUrl =
    this.sanitizer.bypassSecurityTrustResourceUrl("");
  @ViewChild("yonyxAppFrame") public yonyxAppFrame: ElementRef;
  @ViewChild("yonyxEmbed") public yonyxEmbed: ElementRef;

  guidanceStepTitle: string = null;
  guidanceStepBody: string = null;
  guidanceStepQuestion: string = null;

  createWorkOrderTitle: string = null;
  createWorkOrderBody: string = null;
  createWorkOrderQuestion: string = null;

  areUnsubmittedRetriesPending = false;
  isServerRequestPending = false;
  isWorkOrderPteEligible = false;
  submittedSuccessfully = false;
  hideYonyxEmbed = true;
  bShowFormGuidanceStep = false;
  bShowFormCreateWorkOrder = false;
  bDisableLoadGuide = true;
  projectGuid: Guid;
  problemDescription = null;

  stepId = null;
  transcriptId = null;
  currentGuidanceFamilyUrl = null;
  previousSteps = [];
  transcriptIds = [];
  transcriptUrls = "";
  constOriginDownstream = "https://triconah.yonyx.com";
  sessionPlaceholdersMap = { "create-work-order": "" };
  responses: any[];

  briefDesc: string = null;
  category: string = null;
  techVendor: string = null;
  subcategory: string = null;
  subcategory2: string = null;
  problemDesc: string = null;
  priority: string = null;
  tempHighBool: boolean = null;
  tempLowBool: boolean = null;
  result: string = null;
  techSkill: string = null;
  techSkillLevel: number;
  safety: string = null;
  transcript = null;

  triForceRequestCreated = false;

  constructor(
    private router: Router,
    private workOrderService: WorkOrderService,
    public sanitizer: DomSanitizer,
    public errorService: ErrorService,
    public dialog: MatDialog
  ) {
    this.workOrderService.getYonyxGuides().subscribe(
      (result) => this.onGetYonyxGuides(result),
      (error) => this.handleError(error)
    );
  }

  canDeactivate(url: string): boolean {
    if (this.areUnsubmittedRetriesPending) {
      this.showCompleteRequestDialog(url);
      return false;
    } else {
      return true;
    }
  }

  showCompleteRequestDialog(url: string): void {
    const dialogConfig = new MatDialogConfig();
    dialogConfig.width = "350px";
    const dialogRef = this.dialog.open(
      WorkOrderConfirmRequestDialogComponent,
      dialogConfig
    );
    dialogRef.afterClosed().subscribe((result) => {
      this.onDialogClose(result, url);
    });
  }

  onCanCreateWorkOrderResponse(canCreate: boolean): void {
    if (!canCreate) {
      this.router.navigate(["/maintenance/dashboard"]);
    }
  }

  showPteDialog(afterClosed: (dialogResult: string) => void): void {
    const dialogConfig: MatDialogConfig = {
      minWidth: "350px",
      maxWidth: "500px",
      closeOnNavigation: false,
      disableClose: false
    };
    const dialogRef = this.dialog.open(
      WorkOrderGrantPteDialogComponent,
      dialogConfig
    );
    dialogRef.afterClosed().subscribe(afterClosed);
  }

  onDialogClose(dialogResult: string, navigateUrl: string): void {
    if (dialogResult === "COMPLETE_REQUEST") {
      if (this.isWorkOrderPteEligible) {
        this.showPteDialog((dialogResult: string): void => {
          if (dialogResult === "GRANT_PTE") {
            this.createWorkOrder(true);
          } else if (dialogResult === "DENY_PTE") {
            this.createWorkOrder(false);
          } else {
            throw "Unrecognized response from PTE dialog";
          }
        });
      } else {
        // just submit without PTE
        this.createWorkOrder(false);
      }
    } else if (dialogResult === "DISCARD") {
      this.areUnsubmittedRetriesPending = false;
      this.router.navigateByUrl(navigateUrl);
    } else {
      this.areUnsubmittedRetriesPending = true;
    }
  }

  redirect(url: string): void {
    if (url.includes("maintenance/dashboard")) {
      localStorage.setItem("maintenance-dashboard-refresh", "true");
      setTimeout(() => this.router.navigate(["/maintenance/dashboard"]), 1000);
    } else {
      setTimeout(() => this.router.navigateByUrl(url), 1000);
    }
  }

  afterCreateWorkOrders(wasPteGranted: boolean): void {
    this.submittedSuccessfully = true;
    this.areUnsubmittedRetriesPending = false;
    if (wasPteGranted) {
      this.showMessage("Thank you. We will text you with your work order's scheduled date and time window.");
    } else {
      this.showMessage("Thank you. A Tricon Agent will reach out to you to schedule your work order.");
    }

    localStorage.setItem("maintenance-dashboard-refresh", "true");
    setTimeout(() => this.router.navigate(["/maintenance/dashboard"]), 1000);
  }

  completeRequest(): void {
    if (!this.isServerRequestPending) {
      this.areUnsubmittedRetriesPending = true;
      this.saveRetry(
        this.createPayload(),
        (result: WorkOrderSaveRetryResponse): void => {
          if (result.IsWorkOrderEligibleForPte) {
            this.isWorkOrderPteEligible = true;
          }

          if (this.isWorkOrderPteEligible) {
            this.showPteDialog((dialogResult: string): void => {
              if (dialogResult === "GRANT_PTE") {
                this.createWorkOrder(true);
              } else if (dialogResult === "DENY_PTE") {
                this.createWorkOrder(false);
              } else {
                throw "Unrecognized response from PTE dialog";
              }
            });
          } else {
            // just submit without PTE
            this.createWorkOrder(false);
          }
        }
      );
    }
  }

  onGetYonyxGuides(guides: YonyxGuide[]): void {
    this.yonyxGuides = guides;
    if (guides.length < 1) {
      this.errorService.showError("No guides were found");
    }
  }

  ngOnInit(): void {
    this.workOrderService.canCreateWorkOrder().subscribe(
      (res: boolean) => this.onCanCreateWorkOrderResponse(res),
      (err) => this.handleError(err)
    );

    window.addEventListener("message", this.listener.bind(this), false);
    const projectGuid = localStorage.getItem("project-guid");
    if (projectGuid) {
      this.projectGuid = Guid.parse(projectGuid);
    } else {
      this.projectGuid = Guid.create();
    }

    this.LoadGuide();
  }

  previousStep(): void {
    var previousGuidanceFamilyUrl = this.previousSteps.pop();
    if (previousGuidanceFamilyUrl) {
      this.yonyxAppFrameSrc = this.sanitizer.bypassSecurityTrustResourceUrl(
        previousGuidanceFamilyUrl
      );
    } else {
      this.reset();
      this.selectedGuide = null;
    }
  }

  onSelectGuide(): void {
    this.bDisableLoadGuide = false;
  }

  reset(): void {
    this.guidanceStepTitle = null;
    this.guidanceStepBody = null;
    this.guidanceStepQuestion = null;

    this.createWorkOrderTitle = null;
    this.createWorkOrderBody = null;
    this.createWorkOrderQuestion = null;

    this.hideYonyxEmbed = true;
    this.bShowFormGuidanceStep = false;
    this.bShowFormCreateWorkOrder = false;
    this.bDisableLoadGuide = true;
    this.problemDescription = null;

    this.stepId = null;
    this.transcriptId = null;
    this.currentGuidanceFamilyUrl = null;
    this.previousSteps = [];
    this.transcriptIds = [];
    this.transcriptUrls = "";

    this.responses = [];

    this.briefDesc = null;
    this.category = null;
    this.techVendor = null;
    this.subcategory = null;
    this.subcategory2 = null;
    this.problemDesc = null;
    this.priority = null;
    this.tempHighBool = null;
    this.tempLowBool = null;
    this.result = null;
    this.techSkill = null;
    this.techSkillLevel = null;
    this.safety = null;
    this.transcript = null;
  }

  createAnother(): void {
    this.saveRetry(
      this.createPayload(),
      (result: WorkOrderSaveRetryResponse): void => {
        this.areUnsubmittedRetriesPending = true;
        if (result.IsWorkOrderEligibleForPte) {
          this.isWorkOrderPteEligible = true;
        }

        this.triForceRequestCreated = this.wasTriForceRequestCreated();
        this.reset();
        this.selectedGuide = null;
      }
    );
  }

  wasTriForceRequestCreated(): boolean {
    return (
      this.techVendor === "vendor" || this.result === "maintenance project"
    );
  }

  createPayload(): WorkOrderCreate {
    return new WorkOrderCreate(
      this.selectedGuide.CategoryKey,
      this.briefDesc,
      this.subcategory,
      this.problemDescription,
      this.priority,
      this.safety,
      this.techSkillLevel,
      this.techSkill,
      this.projectGuid.toString(),
      this.techVendor,
      this.result,
      this.transcript,
      this.tempHighBool,
      this.tempLowBool,
      this.transcriptId
    );
  }

  saveRetry(
    workOrder: WorkOrderCreate,
    onSuccess: (result: WorkOrderSaveRetryResponse) => void
  ): void {
    this.isServerRequestPending = true;
    this.workOrderService.saveRetry(workOrder).subscribe(
      (result: WorkOrderSaveRetryResponse) => {
        this.isServerRequestPending = false;
        onSuccess(result);
      },
      (err) => this.handleError(err)
    );
  }

  createWorkOrder(
    grantPte: boolean
  ): void {
    this.isServerRequestPending = true;
    this.workOrderService.createWorkOrder(this.projectGuid.toString(), grantPte).subscribe(
      () => {
        this.isServerRequestPending = false;
        this.afterCreateWorkOrders(grantPte);
      },
      (err) => this.handleError(err)
    );
  }

  handleError(err: any): void {
    console.error(err);
    let msg = "";
    if (err.error && err.error.message) {
      msg = err.error.message;
    } else if (err.message) {
      msg = err.message;
    } else {
      msg = "An error occurred.";
    }
    this.errorService.showError(msg);
    this.isServerRequestPending = false;
  }

  showMessage(message: string): void {
    this.errorService.showMessage(message);
  }

  ngOnDestroy(): void {
    window.removeEventListener("message", this.listener, false);
  }

  LoadGuide(): void {
    this.reset();
    this.yonyxAppFrameSrc = this.sanitizer.bypassSecurityTrustResourceUrl(
      `https://triconah.yonyx.com/y/conversation/?id=${this.selectedGuide?.Id}&h=0&st=1`
    );
  }

  onUserResponseClick(id: number): void {
    this.addToPreviousSteps();
    this.postIFrameMessage({ messageName: "enable-user-responses" });
    this.postIFrameMessage({
      messageName: "select-user-response",
      userResponseId: id,
    });
  }

  listener(event: MessageEvent<any>): void {
    if (event.origin && event.origin.includes("triconah.yonyx.com")) {
      try {
        const data = JSON.parse(event.data);
        if (data) {
          if (data.messageName === "guidance-step-loaded") {
            const body = data.body;
            const placeholderNames = data.placeholders;
            this.stepId = data.id;
            this.transcriptId = data.transcriptId;
            this.currentGuidanceFamilyUrl = data.currentGuidanceFamilyUrl;
            this.addTranscriptId(this.transcriptId);
            if (body && body.length > 0) {
              if (placeholderNames && placeholderNames.length > 0) {
                for (let i = 0; i < placeholderNames.length; i++) {
                  if (this.sessionPlaceholdersMap[placeholderNames[i]]) {
                    var keyTmp = "{{" + "ph:" + placeholderNames[i] + "}}";
                    data.body = body.replace(
                      new RegExp(keyTmp, "g"),
                      this.sessionPlaceholdersMap[placeholderNames[i]]
                    );
                  }
                }
              }
            }

            const commandInternalName = data.commandInternalName;
            if (commandInternalName && commandInternalName.length > 0) {
              this.processCommand(
                commandInternalName,
                this.stepId,
                this.transcriptId,
                data
              );
            } else {
              this.showFormGuidanceStep(data);
            }
          } else if (data.messageName === "dimension") {
            if (data.width && data.height) {
              this.yonyxAppFrame.nativeElement.setAttribute(
                "height",
                data.height
              );
            }
          }
        }
      } catch (err) {
        this.errorService.showError(err);
      }
    }
  }

  postIFrameMessage(data: any): void {
    (
      document.getElementById("yonyxAppFrame") as HTMLIFrameElement
    ).contentWindow.postMessage(
      JSON.stringify(data),
      this.constOriginDownstream
    );
  }

  processCommand(
    commandInternalName: string,
    stepId,
    transcriptId,
    data: any
  ): void {
    switch (commandInternalName) {
      case "display-create-work-order":
        this.postIFrameMessage({ messageName: "disable-user-responses" });
        this.showFormCreateWorkOrder(data);
        break;
      default:
    }
  }

  addTranscriptId(transcriptId: any): void {
    if (this.transcriptIds.indexOf(transcriptId) === -1) {
      this.transcriptIds.push(transcriptId);
    }
  }

  hideAll(): void {
    this.bShowFormGuidanceStep = false;
    this.bShowFormCreateWorkOrder = false;
  }

  addToPreviousSteps(): void {
    if (this.previousSteps.length > 0) {
      if (
        this.previousSteps[this.previousSteps.length - 1] !==
        this.currentGuidanceFamilyUrl
      ) {
        this.previousSteps.push(this.currentGuidanceFamilyUrl);
      }
    } else {
      this.previousSteps.push(this.currentGuidanceFamilyUrl);
    }
  }

  showFormGuidanceStep(data: any): void {
    this.hideAll();
    this.bShowFormGuidanceStep = true;
    this.bDisableLoadGuide = true;
    this.guidanceStepBody =
      data.body && data.body.length > 7
        ? data.body.slice(3, data.body.length - 4)
        : "";
    this.guidanceStepQuestion = data.question;
    this.responses = data.responses;
    this.processPlaceholders(data);
  }

  showFormCreateWorkOrder(data: any): void {
    this.processPlaceholders(data);
    this.hideAll();
    this.bShowFormCreateWorkOrder = true;
    this.createWorkOrderTitle = data.title;
    this.createWorkOrderBody = data.body;
    this.createWorkOrderQuestion = data.question;
  }

  processPlaceholders(data: any): void {
    const briefDesc = data.placeholderValues.find(
      (x) => x.name === "brief-description"
    );
    if (briefDesc) {
      this.briefDesc = briefDesc.value;
    }
    const category = data.placeholderValues.find((x) => x.name === "category");
    if (category) {
      this.category = category.value;
    }
    const techVendor = data.placeholderValues.find(
      (x) => x.name === "tech-vendor"
    );
    if (techVendor) {
      this.techVendor = techVendor.value;
    }
    const subcategory = data.placeholderValues.find(
      (x) => x.name === "subcategory"
    );
    if (subcategory) {
      this.subcategory = subcategory.value;
    }
    const subcategory2 = data.placeholderValues.find(
      (x) => x.name === "subcategory2"
    );
    if (subcategory2) {
      this.subcategory2 = subcategory2.value;
    }
    const problemDesc = data.placeholderValues.find(
      (x) => x.name === "problem-description"
    );
    if (problemDesc) {
      this.problemDesc = problemDesc.value;
    }
    const priority = data.placeholderValues.find((x) => x.name === "priority");
    if (priority) {
      this.priority = priority.value;
    }
    const tempHighBool = data.placeholderValues.find(
      (x) => x.name === "temp-high"
    );
    if (tempHighBool) {
      this.tempHighBool = true;
    }
    const tempLowBool = data.placeholderValues.find(
      (x) => x.name === "temp-low"
    );
    if (tempLowBool) {
      this.tempLowBool = true;
    }
    const result = data.placeholderValues.find((x) => x.name === "result");
    if (result) {
      this.result = result.value;
    }
    const techSkill = data.placeholderValues.find(
      (x) => x.name === "tech-skill"
    );
    if (techSkill) {
      this.techSkill = techSkill.value;
    }
    const techSkillLevel = data.placeholderValues.find(
      (x) => x.name === "tech-skill-level"
    );
    if (techSkillLevel) {
      this.techSkillLevel = techSkillLevel.value;
    }
    const safety = data.placeholderValues.find((x) => x.name === "safety");
    if (safety) {
      this.safety = safety.value;
    }
  }
}
